import React from 'react';
import { toast } from 'sonner';
import { Copy, CheckCircle2, CircleDashed, AlertTriangle } from 'lucide-react';
import { Button } from '../../ui/button';

const copy = (text, what) => navigator.clipboard.writeText(text).then(() => toast.success(`${what} copied`)).catch(() => toast.error('Copy failed: select and copy it manually'));

const envState = (d) => {
  if (d.needs_reverify) return ['bad', 'Needs re-verification for the current domain'];
  if (d.webhook.status === 'verified') return ['ok', `Verified${d.webhook.verified_at ? ` ${new Date(d.webhook.verified_at).toLocaleString()}` : ''}`];
  if (d.webhook.status === 'pending') return ['wait', 'Waiting for the check event from Stripe…'];
  const ep = d.endpoint;
  if (ep && !ep.found) return ['bad', 'Endpoint not found in this Stripe mode'];
  if (ep?.missing_events?.length) return ['bad', `Endpoint is missing: ${ep.missing_events.join(', ')}`];
  if (d.secret_key.status === 'missing') return ['bad', 'Add the secret key first'];
  return ['wait', 'Not verified yet'];
};
const ICON = { ok: [CheckCircle2, 'text-emerald-700'], wait: [CircleDashed, 'text-amber-700'], bad: [AlertTriangle, 'text-red-600'] };

const CopyRow = ({ value, what, testId }) => (
  <div className="flex items-center gap-2 bg-neutral-50 border rounded-md px-3 py-1.5">
    <code className="text-xs flex-1 break-all" data-testid={`${testId}-value`}>{value}</code>
    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => copy(value, what)} data-testid={`${testId}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
  </div>
);

export const WebhookSetupGuide = ({ data }) => {
  const url = data.webhook_url;
  const events = data.required_events || [];
  return (
    <div className="border rounded-lg p-4 space-y-3" data-testid="webhook-setup-guide">
      <p className="font-semibold">Stripe Webhook Setup Guide</p>
      {!url ? <p className="text-sm text-red-700" data-testid="webhook-guide-no-domain">Set the website (Primary Domain) in Company Profile first: the webhook address is built from it.</p> : (
        <>
          <div><p className="text-xs uppercase text-neutral-500 mb-1">Endpoint URL</p><CopyRow value={url} what="Webhook URL" testId="webhook-guide-url" /></div>
          <div>
            <div className="flex items-center justify-between mb-1"><p className="text-xs uppercase text-neutral-500">Events to send ({events.length})</p>
              <Button size="sm" variant="outline" className="h-7" onClick={() => copy(events.join('\n'), 'All events')} data-testid="webhook-guide-copy-all">Copy all</Button></div>
            <div className="space-y-1">{events.map((e) => <CopyRow key={e} value={e} what={e} testId={`webhook-guide-event-${e.replace(/\./g, '-')}`} />)}</div>
          </div>
        </>
      )}
      <ol className="list-decimal ml-5 text-sm text-neutral-700 space-y-1">
        <li>In Stripe, open Developers → Webhooks (do it once in Test mode and once in Live mode).</li>
        <li>Add an endpoint with the URL above and select the {events.length} events listed.</li>
        <li>Copy that endpoint's signing secret (whsec_…) into Edit Stripe Credentials for the same mode.</li>
        <li>Click "Verify again" for that mode. The site sends a no-charge check event and marks the webhook Verified when it arrives.</li>
      </ol>
      <div className="grid sm:grid-cols-2 gap-2">
        {['test', 'live'].map((env) => {
          const [k, text] = envState(data[env]);
          const [Icon, tone] = ICON[k];
          return (
            <div key={env} className="flex items-start gap-2 text-sm border rounded-md p-2" data-testid={`webhook-guide-status-${env}`}>
              <Icon className={`w-4 h-4 mt-0.5 ${tone}`} /><div><p className="font-medium capitalize">{env} webhook</p><p className="text-xs text-neutral-600" data-testid={`webhook-guide-status-${env}-text`}>{text}</p></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
